import { db, storage } from '@/firebase/config'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  increment,
  serverTimestamp
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'

const EVENTS_COLLECTION = 'events'
const REGISTRATIONS_COLLECTION = 'event_registrations'
const COMMENTS_COLLECTION = 'event_comments'
const PHOTOS_COLLECTION = 'event_photos'
const ACHIEVEMENTS_COLLECTION = 'user_achievements'

// =============== Event Management ===============

export const createEvent = async (eventData, coverImageFile) => {
  try {
    let imageUrl = ''

    // Upload cover image if provided
    if (coverImageFile) {
      const storageRef = ref(storage, `events/${Date.now()}_${coverImageFile.name}`)
      await uploadBytes(storageRef, coverImageFile)
      imageUrl = await getDownloadURL(storageRef)
    }

    const event = {
      ...eventData,
      image_url: imageUrl,
      participants: 0,
      created_at: serverTimestamp(),
      status: 'active'
    }

    const docRef = await addDoc(collection(db, EVENTS_COLLECTION), event)
    return { id: docRef.id, ...event }
  } catch (error) {
    console.error('Error creating event:', error)
    throw error
  }
}

export const updateEvent = async (eventId, eventData, newCoverImageFile) => {
  try {
    const updateData = { ...eventData }

    // Upload new cover image if provided
    if (newCoverImageFile) {
      const storageRef = ref(storage, `events/${Date.now()}_${newCoverImageFile.name}`)
      await uploadBytes(storageRef, newCoverImageFile)
      updateData.image_url = await getDownloadURL(storageRef)
    }

    updateData.updated_at = serverTimestamp()

    await updateDoc(doc(db, EVENTS_COLLECTION, eventId), updateData)
    return { id: eventId, ...updateData }
  } catch (error) {
    console.error('Error updating event:', error)
    throw error
  }
}

export const deleteEvent = async (eventId) => {
  try {
    await deleteDoc(doc(db, EVENTS_COLLECTION, eventId))
  } catch (error) {
    console.error('Error deleting event:', error)
    throw error
  }
}

export const getEvent = async (eventId) => {
  try {
    const docRef = doc(db, EVENTS_COLLECTION, eventId)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() }
    }
    return null
  } catch (error) {
    console.error('Error getting event:', error)
    throw error
  }
}

// Alias for getEvent
export const getEventById = getEvent

export const getAllEvents = async (filters = {}) => {
  try {
    // Simple query without complex indexes
    const q = collection(db, EVENTS_COLLECTION)
    const querySnapshot = await getDocs(q)

    let results = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))

    // Filter in JavaScript to avoid Firestore index requirements
    if (filters.category) {
      results = results.filter(event => event.category === filters.category)
    }

    if (filters.status) {
      results = results.filter(event => event.status === filters.status)
    } else {
      // Default: only show active events
      results = results.filter(event => event.status === 'active')
    }

    // Sort by time_start
    results.sort((a, b) => {
      const timeA = a.time_start?.seconds || 0
      const timeB = b.time_start?.seconds || 0
      return filters.order === 'desc' ? timeB - timeA : timeA - timeB
    })

    // Apply limit if specified
    if (filters.limit) {
      results = results.slice(0, filters.limit)
    }

    return results
  } catch (error) {
    console.error('Error getting events:', error)
    throw error
  }
}

// =============== Event Registration ===============

export const registerForEvent = async (eventId, userId, userData) => {
  try {
    // Check if already registered
    const existingRegistration = await getRegistration(eventId, userId)
    if (existingRegistration && existingRegistration.status === 'active') {
      throw new Error('Already registered for this event')
    }

    // Check event capacity
    const event = await getEvent(eventId)
    if (event.participants >= event.capacity) {
      throw new Error('Event is full')
    }

    // Create registration
    const registration = {
      event_id: eventId,
      user_id: userId,
      user_name: userData.name,
      user_email: userData.email,
      user_phone: userData.phone || '',
      status: 'active',
      registered_at: serverTimestamp()
    }

    const docRef = await addDoc(collection(db, REGISTRATIONS_COLLECTION), registration)

    // Increment participant count
    await updateDoc(doc(db, EVENTS_COLLECTION, eventId), {
      participants: increment(1)
    })

    return { id: docRef.id, ...registration }
  } catch (error) {
    console.error('Error registering for event:', error)
    throw error
  }
}

export const cancelRegistration = async (eventId, userId) => {
  try {
    const registration = await getRegistration(eventId, userId)
    if (!registration) {
      throw new Error('Registration not found')
    }

    // Update registration status
    await updateDoc(doc(db, REGISTRATIONS_COLLECTION, registration.id), {
      status: 'canceled',
      canceled_at: serverTimestamp()
    })

    // Decrement participant count
    await updateDoc(doc(db, EVENTS_COLLECTION, eventId), {
      participants: increment(-1)
    })

    return true
  } catch (error) {
    console.error('Error canceling registration:', error)
    throw error
  }
}

export const getRegistration = async (eventId, userId) => {
  try {
    // Simplified query to avoid index requirement
    const q = query(
      collection(db, REGISTRATIONS_COLLECTION),
      where('event_id', '==', eventId),
      where('user_id', '==', userId)
    )

    const querySnapshot = await getDocs(q)
    if (!querySnapshot.empty) {
      // Filter for active status in JavaScript instead of Firestore query
      const activeRegistration = querySnapshot.docs.find(doc => doc.data().status === 'active')
      if (activeRegistration) {
        return { id: activeRegistration.id, ...activeRegistration.data() }
      }
    }
    return null
  } catch (error) {
    console.error('Error getting registration:', error)
    throw error
  }
}

export const getUserRegistrations = async (userId) => {
  try {
    // Simplified query to avoid complex index requirement
    const q = query(
      collection(db, REGISTRATIONS_COLLECTION),
      where('user_id', '==', userId)
    )

    const querySnapshot = await getDocs(q)
    // Filter and sort in JavaScript
    const registrations = querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(reg => reg.status === 'active')
      .sort((a, b) => {
        const timeA = a.registered_at?.seconds || 0
        const timeB = b.registered_at?.seconds || 0
        return timeB - timeA
      })

    return registrations
  } catch (error) {
    console.error('Error getting user registrations:', error)
    throw error
  }
}

export const getEventRegistrations = async (eventId) => {
  try {
    // Simplified query to avoid complex index requirement
    const q = query(
      collection(db, REGISTRATIONS_COLLECTION),
      where('event_id', '==', eventId)
    )

    const querySnapshot = await getDocs(q)
    // Filter and sort in JavaScript
    const registrations = querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(reg => reg.status === 'active')
      .sort((a, b) => {
        const timeA = a.registered_at?.seconds || 0
        const timeB = b.registered_at?.seconds || 0
        return timeB - timeA
      })

    return registrations
  } catch (error) {
    console.error('Error getting event registrations:', error)
    throw error
  }
}

// =============== Event Comments ===============

export const addComment = async (eventId, userId, userName, content) => {
  try {
    const comment = {
      event_id: eventId,
      user_id: userId,
      user_name: userName,
      content,
      likes: 0,
      created_at: serverTimestamp()
    }

    const docRef = await addDoc(collection(db, COMMENTS_COLLECTION), comment)
    return { id: docRef.id, ...comment }
  } catch (error) {
    console.error('Error adding comment:', error)
    throw error
  }
}

export const deleteComment = async (commentId) => {
  try {
    await deleteDoc(doc(db, COMMENTS_COLLECTION, commentId))
  } catch (error) {
    console.error('Error deleting comment:', error)
    throw error
  }
}

export const getEventComments = async (eventId) => {
  try {
    // Simplified query to avoid index requirement
    const q = query(
      collection(db, COMMENTS_COLLECTION),
      where('event_id', '==', eventId)
    )

    const querySnapshot = await getDocs(q)
    // Sort in JavaScript instead of Firestore
    const comments = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))

    comments.sort((a, b) => {
      const timeA = a.created_at?.seconds || 0
      const timeB = b.created_at?.seconds || 0
      return timeB - timeA // desc order
    })

    return comments
  } catch (error) {
    console.error('Error getting comments:', error)
    throw error
  }
}

export const likeComment = async (commentId) => {
  try {
    await updateDoc(doc(db, COMMENTS_COLLECTION, commentId), {
      likes: increment(1)
    })
  } catch (error) {
    console.error('Error liking comment:', error)
    throw error
  }
}

// =============== Event Photos ===============

export const uploadEventPhoto = async (eventId, userId, userName, imageFile, caption) => {
  try {
    // Check photo limit per user (max 5)
    const userPhotos = await getUserEventPhotos(eventId, userId)
    if (userPhotos.length >= 5) {
      throw new Error('Maximum 5 photos per user')
    }

    // Upload image
    const storageRef = ref(storage, `event_photos/${eventId}/${Date.now()}_${imageFile.name}`)
    await uploadBytes(storageRef, imageFile)
    const photoUrl = await getDownloadURL(storageRef)

    // Create photo document
    const photo = {
      event_id: eventId,
      user_id: userId,
      user_name: userName,
      photo_url: photoUrl,
      caption: caption || '',
      likes: 0,
      created_at: serverTimestamp()
    }

    const docRef = await addDoc(collection(db, PHOTOS_COLLECTION), photo)
    return { id: docRef.id, ...photo }
  } catch (error) {
    console.error('Error uploading photo:', error)
    throw error
  }
}

export const deleteEventPhoto = async (photoId, photoUrl) => {
  try {
    // Delete from storage
    const storageRef = ref(storage, photoUrl)
    await deleteObject(storageRef)

    // Delete from firestore
    await deleteDoc(doc(db, PHOTOS_COLLECTION, photoId))
  } catch (error) {
    console.error('Error deleting photo:', error)
    throw error
  }
}

export const getEventPhotos = async (eventId, sortBy = 'latest') => {
  try {
    // Simplified query to avoid index requirement
    const q = query(
      collection(db, PHOTOS_COLLECTION),
      where('event_id', '==', eventId)
    )

    const querySnapshot = await getDocs(q)
    const photos = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))

    // Sort in JavaScript instead of Firestore
    if (sortBy === 'popular') {
      photos.sort((a, b) => (b.likes || 0) - (a.likes || 0))
    } else {
      photos.sort((a, b) => {
        const timeA = a.created_at?.seconds || 0
        const timeB = b.created_at?.seconds || 0
        return timeB - timeA // desc order
      })
    }

    return photos
  } catch (error) {
    console.error('Error getting event photos:', error)
    throw error
  }
}

export const getUserEventPhotos = async (eventId, userId) => {
  try {
    const q = query(
      collection(db, PHOTOS_COLLECTION),
      where('event_id', '==', eventId),
      where('user_id', '==', userId)
    )

    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error getting user photos:', error)
    throw error
  }
}

export const likePhoto = async (photoId) => {
  try {
    await updateDoc(doc(db, PHOTOS_COLLECTION, photoId), {
      likes: increment(1)
    })
  } catch (error) {
    console.error('Error liking photo:', error)
    throw error
  }
}

// =============== User Achievements ===============

export const checkAndAwardAchievements = async (userId) => {
  try {
    // Get user's event participation count
    const registrations = await getUserRegistrations(userId)
    const participationCount = registrations.length

    // Get user's photo uploads count
    const q = query(
      collection(db, PHOTOS_COLLECTION),
      where('user_id', '==', userId)
    )
    const photoSnapshot = await getDocs(q)
    const photoCount = photoSnapshot.size

    const achievements = []

    // Award achievements based on criteria
    if (participationCount >= 3) {
      achievements.push({
        type: 'health_starter',
        title: '健康起步者',
        description: '参加了3次活动',
        icon: 'bi-award'
      })
    }

    if (participationCount >= 10) {
      achievements.push({
        type: 'health_expert',
        title: '健康达人',
        description: '参加了10次活动',
        icon: 'bi-trophy'
      })
    }

    if (photoCount >= 5) {
      achievements.push({
        type: 'community_sharer',
        title: '社区分享者',
        description: '上传了5次活动照片',
        icon: 'bi-camera'
      })
    }

    // Save achievements to user's collection
    for (const achievement of achievements) {
      const achievementRef = doc(db, ACHIEVEMENTS_COLLECTION, `${userId}_${achievement.type}`)
      const achievementDoc = await getDoc(achievementRef)

      if (!achievementDoc.exists()) {
        await addDoc(collection(db, ACHIEVEMENTS_COLLECTION), {
          user_id: userId,
          ...achievement,
          earned_at: serverTimestamp()
        })
      }
    }

    return achievements
  } catch (error) {
    console.error('Error checking achievements:', error)
    throw error
  }
}

export const getUserAchievements = async (userId) => {
  try {
    // Simplified query to avoid index requirement
    const q = query(
      collection(db, ACHIEVEMENTS_COLLECTION),
      where('user_id', '==', userId)
    )

    const querySnapshot = await getDocs(q)
    const achievements = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))

    // Sort in JavaScript instead of Firestore
    achievements.sort((a, b) => {
      const timeA = a.earned_at?.seconds || 0
      const timeB = b.earned_at?.seconds || 0
      return timeB - timeA // desc order
    })

    return achievements
  } catch (error) {
    console.error('Error getting achievements:', error)
    throw error
  }
}
